import React, { useEffect, JSX, useMemo } from "react";
import { ButtonIcon } from "components/buttons/button-icon/button-icon";
import { ButtonSize, ButtonStyle, ButtonType } from "components/buttons/button/button";
import { Icons } from "components/icons/constants/icons";
import { HeaderBanner, HeaderBannerNavItem } from "components/header-banner/header-banner";
import { SideContentLeftLayout } from "components/layouts/side-content-left-layout/side-content-left-layout";
import { Paragraph, ParagraphStyle } from "components/typography/paragraph/paragraph";
import { DataTable } from "components/tables/data-table/data-table";
import { EmptyText } from "components/empty-text/empty-text";
import { HeaderBannerUtils } from "utilities/header-banner-utils";
import { ContextMenu } from "components/context-menu/context-menu/context-menu";
import { Pager } from "components/pager/pager";
import { ContextMenuAnchor } from "components/context-menu/context-menu-anchor/context-menu-anchor";
import { RouteUtils } from "utilities/route-utils";
import { sitemap } from "sitemap";
import { SkipNavContent } from "@chakra-ui/skip-nav";
import { Badge } from "components/badges/badge/badge";
import { useUsers } from "utilities/hooks/models/users/use-users";
import { RoleType } from "models/enumerations/users/role-type";
import { useUserSearch } from "utilities/contexts/search/user/user-search-context";
import { CollectionUtils } from "utilities/collection-utils";
import { validatePageAccess } from "utilities/decorators/aspects/authorization/validate-page-access";
import { AccessControlKeys } from "utilities/enumerations/authorization/access-control-keys";
import { ScrollUtils } from "utilities/scroll-utils";
import { StringUtils } from "utilities/string-utils";
import { UserRoleRecord } from "models/view-models/user-roles/user-role-record";
import { Anchor } from "../../../../../components/typography/anchors/anchor/anchor";
import UserFilterPage from "../user-filter-component/user-filter-component";
import { t } from "utilities/localization/t";
import "./user-list-page.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface UserListPageProps {}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME = "user-list-page";
const ITEMS_PER_PAGE = 20;

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const UserListPage: React.FC<UserListPageProps> = validatePageAccess(
    AccessControlKeys.UserListPage
)((): JSX.Element => {
    const navItems: HeaderBannerNavItem[] = HeaderBannerUtils.getUserManagementNavItems();
    const { initialCriteria: criteria, setCriteria } = useUserSearch();

    const roleTypes = useMemo(
        () =>
            criteria?.roleType == null || criteria.roleType === -1
                ? undefined
                : [criteria.roleType as RoleType],
        [criteria?.roleType]
    );
    
    const {
        users,
        rowCount: userCount,
        isLoading,
    } = useUsers({
        hasNoUserRoles: criteria?.roleType === -1,
        roleTypes: roleTypes,
        emailAddress: criteria?.emailSearchText,
        firstName: criteria?.firstNameSearchText,
        lastName: criteria?.lastNameSearchText,
        id: criteria?.idSearchValue,
        includeLastLogin: true,
        includeRoles: true,
        skip: ((criteria?.currentPage ?? 1) - 1) * ITEMS_PER_PAGE,
        take: ITEMS_PER_PAGE,
    });

    const onPageClick = (pageNumber: number) => {
        setCriteria((prev) => ({
            ...prev,
            currentPage: pageNumber,
        }));
    };

    const getViewUserPath = (userId: number) => {
        return RouteUtils.replacePathParams(sitemap.admin.userManagement.users.info.default, {
            id: userId,
        });
    };

    useEffect(() => {
        ScrollUtils.scrollToElementBySelector(`.content`);
    }, [users]);

    return (
        <div className={CSS_CLASS_NAME}>
            <HeaderBanner
                dataTestId={`user-list-page:user-management:header`}
                navItems={navItems}
                title={t("userManagement")}>
                <ButtonIcon
                    text={t("addNewUser")}
                    buttonSize={ButtonSize.Medium}
                    buttonStyle={ButtonStyle.TertiaryAlt}
                    buttonType={ButtonType.Link}
                    dataTestId={`user-list-page:add-new-user:btn`}
                    iconType={Icons.Plus}
                    linkPath={sitemap.admin.userManagement.users.new}
                />
            </HeaderBanner>
            <SideContentLeftLayout
                sidebarElement={
                    <div className={`${CSS_CLASS_NAME}__sidebar`}>
                        <Paragraph style={ParagraphStyle.Label}>{t("filterResults")}</Paragraph>
                        
                        <UserFilterPage
                            isLoading={isLoading}
                        />
                    </div>
                }>
                <div className="content-wrap">
                    <div className="content">
                        <SkipNavContent>
                            {users?.length > 0 && (
                                <DataTable dataTestId="user-list-page:data:table">
                                    <thead>
                                        <tr>
                                            <th className="id">{t("id")}</th>
                                            <th className="name">{t("name")}</th>
                                            <th className="email">{t("email")}</th>
                                            <th className="netsuiteId">{t("netsuiteID")}</th>
                                            <th className="role">{t("role")}</th>
                                            <th className="last-login">{t("lastLoginDate")}</th>
                                            <th className="action">
                                                <span className="sr-only">{t("action")}</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((user, index) => (
                                            <tr
                                                data-testid={`user-list-page:user-table:row:${user.id}`}
                                                key={`user-row-${user.id}`}>
                                                <td
                                                    className="id"
                                                    data-testid={`user-list-page:user-table:data:${user.id}:id`}>
                                                    {user.id}
                                                </td>
                                                <td
                                                    className="name"
                                                    data-testid={`user-list-page:user-table:data:${user.id}:name`}>
                                                    {StringUtils.valueOrDefault(
                                                        user.getFirstAndLastName()
                                                    )}
                                                </td>
                                                <td
                                                    className="email"
                                                    data-testid={`user-list-page:user-table:data:${user.id}:email`}>
                                                    <Anchor
                                                        path={getViewUserPath(user.id ?? 0)}
                                                        text={user.email}
                                                    />
                                                </td>
                                                <td
                                                    className="netsuiteId"
                                                    data-testid={`user-list-page:user-table:data:${user.id}:netsuite`}>
                                                    {user.netsuiteId ?? "--"}
                                                </td>
                                                <td className="role">
                                                    <div
                                                        className="user-roles"
                                                        data-testid={`user-list-page:user-table:data:${user.id}:role`}>
                                                        {!CollectionUtils.hasValues(
                                                            user.userRoles
                                                        ) && <Badge text={t("unassigned")} />}
                                                        {user.userRoles?.map(
                                                            (userRole: UserRoleRecord) => (
                                                                <Badge
                                                                    key={userRole.id}
                                                                    text={userRole.role?.name}
                                                                />
                                                            )
                                                        )}
                                                    </div>
                                                </td>
                                                <td
                                                    className="last-login"
                                                    data-testid={`user-list-page:user-table:data:${user.id}:last-login`}>
                                                    {user.invitePending() ||
                                                    CollectionUtils.isEmpty(user.userLogins) ? (
                                                        <Badge text={t("invitePending")} />
                                                    ) : (
                                                        user.getLastLoginText()
                                                    )}
                                                </td>
                                                <td
                                                    className="action"
                                                    data-testid={`user-list-page:user:data:${user.id}:action`}>
                                                    <ContextMenu
                                                        dataTestId={`user-list-page:user:data:${user.id}:action`}>
                                                        <ContextMenuAnchor
                                                            dataTestId={`user-list-page:user:data:${user.id}:action`}
                                                            hrefPath={getViewUserPath(user.id ?? 0)}
                                                            displayName={t("viewUserInfo")}
                                                        />
                                                    </ContextMenu>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </DataTable>
                            )}
                            {(users == null || users.length === 0) && (
                                <EmptyText table>{t("noResultsFoundPleaseTryAgain")}</EmptyText>
                            )}
                        </SkipNavContent>
                    </div>
                </div>
                {users != null && users.length > 0 && (
                    <div className="footer">
                        <Pager
                            currentPage={criteria?.currentPage ?? 1}
                            totalPages={userCount / ITEMS_PER_PAGE}
                            onPageClick={onPageClick}
                            itemsPerPage={ITEMS_PER_PAGE}
                            totalItems={userCount}
                        />
                    </div>
                )}
            </SideContentLeftLayout>
        </div>
    );
});

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { UserListPage };

// #endregion Exports
